import { cn } from "../../utils/common/cn";

/**
 * A small circular pending-count marker, positioned at the top-right corner of whatever it's
 * given as a sibling — a Button, an icon, anything. Renders nothing at count <= 0, so a
 * caller never has to gate on "0 pending" itself.
 *
 * Usage: wrap the target element in `<span className="relative inline-flex">`, put the
 * element and <CountBadge /> as siblings inside it. The wrapper — not this component — owns
 * `position: relative`, so this never has to know what it's decorating.
 */
export function CountBadge({
  count,
  cap = 9,
  className,
}: {
  count: number;
  /** Highest number shown as itself; anything above renders "<cap>+". */
  cap?: number;
  className?: string;
}) {
  if (count <= 0) return null;
  const label = count > cap ? `${cap}+` : String(count);
  return (
    <span
      aria-label={`${count} pending`}
      className={cn(
        "pointer-events-none absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center " +
          "justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold " +
          "leading-none text-destructive-foreground ring-2 ring-background",
        className,
      )}
    >
      {label}
    </span>
  );
}
